<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import {
  DV_INSERT_DRAFT_EVENT,
  assistantNoDraftReason,
} from 'dashboard/helper/dvNotes';
import FormattedContent from './FormattedContent.vue';

// DV: an assistant note ("**Черновик…**\n<draft>\n---\n<analysis>") rendered
// as a card: the draft text up front with insert/copy actions, the agent's
// reasoning folded below. ReplyTopPanel handles DV_INSERT_DRAFT_EVENT.

const props = defineProps({
  draft: { type: String, default: '' },
  content: { type: String, default: '' },
  model: { type: String, default: '' },
  intent: { type: String, default: '' },
  // Staff agent drafts (Agent API): who prepared it, the gist, output check
  // findings and technical lines folded under «Подробнее».
  author: { type: String, default: '' },
  summary: { type: String, default: '' },
  findings: { type: Array, default: () => [] },
  details: { type: Array, default: () => [] },
});

const showDetails = ref(false);

const { t } = useI18n();
const showAnalysis = ref(false);

const analysis = computed(() => {
  const separator = props.content.search(/\n-{3,}\s*\n/u);
  if (separator === -1) return '';
  return props.content
    .slice(separator)
    .replace(/^\n-{3,}\s*\n/u, '')
    .trim();
});

const modelLabel = computed(() => {
  const model = String(props.model || '').toLowerCase();
  if (model.includes('opus')) return 'Opus';
  if (model.includes('sonnet')) return 'Sonnet';
  if (model.includes('haiku')) return 'Haiku';
  if (model.includes('claude')) return 'Claude';
  if (model.includes('gpt')) return 'GPT';
  return '';
});

const noDraftReason = computed(() =>
  props.draft ? '' : assistantNoDraftReason(props.content, props.intent)
);

const insertDraft = () => {
  emitter.emit(DV_INSERT_DRAFT_EVENT, props.draft);
};

const copyDraft = async () => {
  try {
    await copyTextToClipboard(props.draft);
    useAlert(t('CONTACT_PANEL.DV_ASSISTANT_DRAFT.COPIED'));
  } catch (error) {
    useAlert(t('CONTACT_PANEL.DV_ASSISTANT_DRAFT.COPY_FAILED'));
  }
};
</script>

<template>
  <div class="flex min-w-0 flex-col gap-2.5">
    <div class="flex items-center gap-2">
      <span class="i-lucide-sparkles size-4 shrink-0 text-n-amber-11" />
      <span class="text-sm font-semibold text-n-amber-12">
        <template v-if="author">Черновик · {{ author }}</template>
        <template v-else>
          {{
            draft
              ? $t('CONTACT_PANEL.DV_ASSISTANT_DRAFT.TITLE')
              : $t('CONTACT_PANEL.DV_ASSISTANT_DRAFT.NO_DRAFT_TITLE')
          }}
        </template>
      </span>
      <span
        v-if="modelLabel"
        class="rounded-md bg-n-amber-4 px-1.5 py-0.5 text-[11px] font-medium leading-4 text-n-amber-11"
      >
        {{ modelLabel }}
      </span>
    </div>

    <p v-if="summary" class="mb-0 text-xs leading-5 text-n-amber-11">
      {{ summary }}
    </p>

    <p
      v-if="!draft"
      class="mb-0 rounded-lg bg-n-solid-1 px-3 py-2 text-sm leading-5 text-n-slate-11"
    >
      {{ noDraftReason }}
    </p>

    <div
      v-if="draft"
      class="whitespace-pre-wrap break-words rounded-lg bg-n-solid-1 px-3 py-2.5 text-sm leading-6 text-n-slate-12 select-text"
    >
      {{ draft }}
    </div>

    <ul
      v-if="findings.length"
      class="mb-0 flex list-none flex-col gap-1 rounded-lg bg-n-ruby-2 px-3 py-2 text-xs leading-5 text-n-ruby-11"
      data-dv-draft-findings
    >
      <li v-for="(finding, index) in findings" :key="index" class="flex gap-1.5">
        <span>{{ finding.severity === 'hard' ? '❗' : '•' }}</span>
        <span>
          {{ finding.message }}
          <template v-if="finding.excerpt"> — «{{ finding.excerpt }}»</template>
        </span>
      </li>
    </ul>

    <div v-if="draft" class="flex flex-wrap items-center gap-2">
      <button
        type="button"
        class="inline-flex h-8 items-center gap-1.5 rounded-lg bg-n-brand px-3 text-sm font-medium text-white transition-colors hover:bg-n-brand/90"
        @click="insertDraft"
      >
        <span class="i-lucide-corner-down-left size-4" />
        {{ $t('CONTACT_PANEL.DV_ASSISTANT_DRAFT.INSERT') }}
      </button>
      <button
        type="button"
        class="inline-flex h-8 items-center gap-1.5 rounded-lg bg-n-alpha-3 px-3 text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-alpha-2"
        @click="copyDraft"
      >
        <span class="i-lucide-copy size-4" />
        {{ $t('CONTACT_PANEL.DV_ASSISTANT_DRAFT.COPY') }}
      </button>
    </div>

    <div v-if="details.length" class="border-t border-n-amber-6 pt-2">
      <button
        type="button"
        class="inline-flex items-center gap-1 text-xs font-medium text-n-amber-11 hover:underline"
        @click="showDetails = !showDetails"
      >
        <span
          class="i-lucide-chevron-right size-3.5 transition-transform"
          :class="{ 'rotate-90': showDetails }"
        />
        Подробнее
      </button>
      <dl v-if="showDetails" class="mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
        <template v-for="[label, value] in details" :key="label">
          <dt class="text-n-amber-11">{{ label }}</dt>
          <dd class="mb-0 break-words text-n-slate-12">{{ value }}</dd>
        </template>
      </dl>
    </div>

    <div v-if="analysis" class="border-t border-n-amber-6 pt-2">
      <button
        type="button"
        class="inline-flex items-center gap-1 text-xs font-medium text-n-amber-11 hover:underline"
        @click="showAnalysis = !showAnalysis"
      >
        <span
          class="i-lucide-chevron-right size-3.5 transition-transform"
          :class="{ 'rotate-90': showAnalysis }"
        />
        {{ $t('CONTACT_PANEL.DV_ASSISTANT_DRAFT.ANALYSIS') }}
      </button>
      <div v-if="showAnalysis" class="mt-2 text-xs opacity-90">
        <FormattedContent :content="analysis" />
      </div>
    </div>
  </div>
</template>
